import { Injectable } from '@angular/core'
import { selectors } from '@employer/app/store/selectors'
import { environment } from '@employer/environments/environment'
import { isNotNil } from '@engineering11/utility'
import { RestApiClient } from '@engineering11/web-api-rest'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, of } from 'rxjs'
import { catchError, filter, map, take } from 'rxjs/operators'
import { DEFAULT_NETWORK_MONITORING_CONFIG, getBaseSuffix, TokenStorage } from 'shared-lib'
import { AssistantPosture } from '../models/assistant-posture.model'
import { AssistantGateService } from './assistant-gate.service'
import { AssistantPostureService } from './assistant-posture.service'
import { GuideService } from './guide.service'

// Onboarding-offered flag under `environment.services.assistant` (URI-versioned). Reconciled 2026-08-12:
// GET v1/user-state/onboarding/offered → `{ offered: boolean }`; POST marks it once (accept or decline),
// so the offer is presented at most once per user.
const ONBOARDING_OFFERED_PATH = 'user-state/onboarding/offered'

/**
 * Wires the first-login auto-offer and the accept/decline resolution. Accept calls `GuideService.start()`
 * (the floating guided walkthrough card that replaced the spotlight tour); decline calls
 * `AssistantPostureService.recordTourCompletion('skipped')`; the offer is shown in guide via
 * `AssistantPostureService.setPosture(AssistantPosture.Guide)`.
 *
 * The onboarding-offered flag (GET/POST at `ONBOARDING_OFFERED_PATH`) is a BACKEND CONTRACT assumption
 * confined to this service (see ../BACKEND-CONTRACT.md). Built on the same `RestApiClient` +
 * `getBaseSuffix` construction as `assistant-posture.service.ts` (`.get`/`.post` return
 * `Observable<AxiosResponse<Res>>`, so bodies are read via `res.data`).
 *
 * Detection is gated behind an authenticated current user (`selectors.getCurrentUserId`, non-nil) so a
 * signed-out session never offers. The read fails closed: any error resolves to "already offered" so an
 * un-provisioned backend never nags the user every login.
 */
@Injectable({ providedIn: 'root' })
export class AssistantOnboardingService {
  private restClient: RestApiClient

  private _offerVisible$ = new BehaviorSubject<boolean>(false)
  /** Whether the accept/decline offer card is currently shown. */
  readonly offerVisible$ = this._offerVisible$.asObservable()

  /** Idempotent guard: the first-login flag read runs at most once per shell lifetime. */
  private evaluated = false

  /** Idempotent guard: resolving the offer (accept/decline) is terminal — a double-tap fires once. */
  private offerResolved = false

  constructor(
    private store: Store,
    private posture: AssistantPostureService,
    private guide: GuideService,
    private assistantGate: AssistantGateService,
    tokenStorage: TokenStorage
  ) {
    this.restClient = new RestApiClient({
      // Reconciled 2026-08-12: assistant is its own service (service-assistant at /assistant).
      baseUrl: environment.services.assistant,
      token: () => tokenStorage.getAccessToken(),
      baseSuffix: getBaseSuffix(environment, 'assistant'),
      ...DEFAULT_NETWORK_MONITORING_CONFIG,
    })
  }

  /**
   * First-login trigger from the authenticated shell. Idempotent: subsequent calls are no-ops. Waits for
   * an authenticated current user (non-nil) AND a resolved entitlement gate, then reads the
   * onboarding-offered flag exactly once. The guided tour is a trial/Plus feature, so a free customer
   * (gate 'upsell') is never auto-offered a tour that would half-work against their reduced navigation.
   */
  evaluateFirstLogin(): void {
    if (this.evaluated) return
    this.evaluated = true
    combineLatest([
      this.store.select(selectors.getCurrentUserId).pipe(filter(isNotNil)),
      this.assistantGate.gate$.pipe(filter(gate => gate !== 'loading')),
    ])
      .pipe(take(1))
      .subscribe(([, gate]) => {
        if (gate === 'entitled') this.readOfferedFlag()
      })
  }

  /**
   * Read the per-user onboarding-offered flag. For a brand-new user (not already offered) set posture to
   * guide and show the offer card. On any read error fail closed (treat as already offered → no offer).
   */
  private readOfferedFlag(): void {
    this.restClient
      // BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md (GET → `{ offered?: boolean }`)
      .get<{ offered?: boolean }>(ONBOARDING_OFFERED_PATH)
      .pipe(
        map(res => res.data?.offered === true),
        catchError(() => of(true)) // fail closed: read error → treat as already offered (never nag)
      )
      .subscribe(alreadyOffered => {
        if (alreadyOffered) return
        this.posture.setPosture(AssistantPosture.Guide)
        this._offerVisible$.next(true)
      })
  }

  /** Accept the offer: hide it, mark the flag, and start the in-pane guided walkthrough. Idempotent. */
  accept(): void {
    if (this.offerResolved) return
    this.offerResolved = true
    this._offerVisible$.next(false)
    this.markOffered()
    void this.guide.start()
  }

  /** Decline the offer: hide it, mark the flag, and record a skip (guide→helper). Idempotent. */
  decline(): void {
    if (this.offerResolved) return
    this.offerResolved = true
    this._offerVisible$.next(false)
    this.markOffered()
    this.posture.recordTourCompletion('skipped')
  }

  /** Tier-independent re-launch of the same walkthrough (no flag change). */
  relaunch(): void {
    void this.guide.start()
  }

  /** Persist the onboarding-offered flag so the offer never returns. Write error is swallowed. */
  private markOffered(): void {
    this.restClient
      // BACKEND CONTRACT (assumption) — see ../BACKEND-CONTRACT.md (POST body `{ offered: true }`)
      .post(ONBOARDING_OFFERED_PATH, { offered: true })
      .pipe(catchError(() => of(null)))
      .subscribe()
  }
}
